import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import MockAdapter from 'axios-mock-adapter';
import Fakedata from '@/api/fakedata/v1';
import DOMPurify from 'dompurify';
import { RouteUrlList, type IRouteUrlList, type IRouteUrlListElement } from '@/libs/RouteList';
import { LoadingOverLay } from '@/libs/LoadingOverLay';
import { ServiceAlert } from '@/libs/ServiceAlert';
import { SessionExpireTimer } from '@/libs/SessionExpireTimer';
import * as Common from '@/libs/Common';
import { SimplePopUpModalType, SimplePopUpBtnType, SimplePopUp, SimplePopUpSize } from './SimplePopUp';
const simplePopUp = new SimplePopUp();
import router from '@/router';
import { useStateErrorMessageStore } from '@/stores/modules/stateErrorMessage';
import { useEsunMB } from '@/composables/useEsunMB';
import { useClientStore } from '@/stores/modules/clientInfo';

/**
 * @todo 預設API 回傳值
 */
interface GeneralResp {
    status: boolean;
    title: string;
    message: string;
    errorCode: string;
}

export type { GeneralResp };

/**
 * @todo API設定預設值
 */
export enum paramTypeEnum {
    true = 'y',
    false = 'n'
}

/**
 * @todo Override Axios Instance
 */
const config = {
    headers: {
        'Content-Type': 'application/json'
    },
    defaults: {
        baseURL: import.meta.env.VITE_BaseUrl,
        timeout: import.meta.env.VITE_CallApiTimeout,
        withCredentials: true
    }
};

/**
 * @todo 更新 XSRF-Token(Cookie) And Set-Session Cookie
 */
const Instance = axios.create(config);
const overlay = new LoadingOverLay();

// 模組層級取得 closeWebView 和 isAppEmbedded
const { closeWebView, isAppEmbedded } = useEsunMB();

// ApiClient.ts 中不要立即 new，而是提供一個 getter
let _serviceAlert: ServiceAlert | null = null;

function GetServiceAlertInstance(): ServiceAlert {
    if (!_serviceAlert) {
        _serviceAlert = new ServiceAlert(RouteUrlList.StateError, isAppEmbedded.value);
    }
    return _serviceAlert;
}

// 絕對到期 timer：與 ServiceAlert 同樣 lazy 建立，避免 module load 時就觸碰需要 pinia 的依賴
let _sessionExpireTimer: SessionExpireTimer | null = null;

function GetSessionExpireTimerInstance(): SessionExpireTimer {
    if (!_sessionExpireTimer) {
        _sessionExpireTimer = new SessionExpireTimer(RouteUrlList.StateError, isAppEmbedded.value);
    }
    return _sessionExpireTimer;
}

/**
 * @todo mock data
 */
if (import.meta.env.VITE_ENABLE_FAKE_DATA === 'true') {
    ((axios: AxiosInstance) => {
        const mock = new MockAdapter(axios, { delayResponse: 500 });

        mock.onAny().reply(function (config): [number, any] {
            const response = Fakedata.find(([method, path, data]) => {
                return (
                    config?.url?.match(new RegExp(`^${path}$`)) &&
                    config.method?.toUpperCase() === (method as string).toUpperCase() &&
                    (Object.keys(data as object).length === 0 || config?.data?.toString() === JSON.stringify(data))
                );
            });

            // Unexpected request, error out
            if (!response) {
                return [500, {}];
            }

            return response.slice(3) as [number, any];
        });
    })(Instance);
}

/**
 * @todo 自定義設定值
 */
if (import.meta.env.DEV) {
    console.table(import.meta.env);
}

Instance.defaults.baseURL = import.meta.env.VITE_WebUrl + import.meta.env.VITE_BaseUrl;
Instance.defaults.timeout = import.meta.env.VITE_CallApiTimeout;
Instance.defaults.withCredentials = true;
Common.ConsoleLog('[ApiClient] Instance.defaults.baseURL : ' + Instance.defaults.baseURL);

/**
 * @todo intercept Request
 */
Instance.interceptors.request.use(
    async (_config) => {
        Common.ConsoleLog(`[ApiClient] Request Start : method=${_config.method}; url=${_config.url}`);
        // 1.取得停止登入訊息 service alert message 預設 true(y) getServiceAlert=y
        const getServiceAlert: paramTypeEnum =
            typeof _config.params?.getServiceAlert !== 'undefined' ? _config.params?.getServiceAlert : paramTypeEnum.true;
        Common.ConsoleLog('[ApiClient] Instance.interceptors.request : getServiceAlert : ' + getServiceAlert);
        if (getServiceAlert === paramTypeEnum.true) {
            await GetServiceAlertInstance().GetAlertMessage();
        }

        // 2.bind XSCRF-token in to request headers
        let requestToken;
        if (import.meta.env.PROD) {
            const requestTokenEle = document.querySelector(`meta[name="${import.meta.env.VITE_XSRFKey_HeaderName}"]`) as HTMLMetaElement;

            if (requestTokenEle && requestTokenEle.content) {
                requestToken = requestTokenEle.content;
            }
        }
        _config.headers[import.meta.env.VITE_XSRFKey_HeaderName] = requestToken;

        // 3.是否顯示遮罩設定(輪詢ajax需能關閉遮罩)於夾帶參數中 預設 true(y) showOverlay=y
        const showOverlay: paramTypeEnum = typeof _config.params?.showOverlay !== 'undefined' ? _config.params?.showOverlay : paramTypeEnum.true;
        Common.ConsoleLog('[ApiClient] Instance.interceptors.request : showOverlay : ' + showOverlay);
        if (showOverlay === paramTypeEnum.true) {
            overlay.show(null, '');
        }

        // 4.xss filter
        // 4.1 params
        if (_config.params) {
            for (const key in _config.params) {
                if (Object.prototype.hasOwnProperty.call(_config.params, key)) {
                    // WAF will Block Request that contains SpecialChar
                    _config.params[key] = Common.ReplaceSpecialChar(
                        // DOMPurify.sanitize('<img src=x onerror=alert(1)//>'); // becomes <img src="x">
                        DOMPurify.sanitize(_config.params[key])
                    );
                }
            }
        }
        // 4.2 data
        if (_config.data) {
            for (const key in _config.data) {
                if (Object.prototype.hasOwnProperty.call(_config.data, key) && typeof _config.data[key] === 'string') {
                    // WAF will Block Request that contains SpecialChar
                    _config.data[key] = DOMPurify.sanitize(_config.data[key]);
                }
            }
        }
        return _config;
    },
    (error) => {
        LoadingOverLay.hideAll();
        return Promise.reject(error);
    }
);
/**
 * @todo intercept Response
 */
Instance.interceptors.response.use(
    (response) => {
        Common.ConsoleLog(`[ApiClient] Response Intercept : method=${response.config.method} url=${response.config.url}`);
        LoadingOverLay.hideAll();

        // Session 絕對到期同步：後端 SessionRemainingHeaderFilter 於已登入 + 有到期戳時才寫此 header；
        // 沒有即代表本次 User Session 已過期。
        try {
            const clientStore = useClientStore();
            const rawHeader: unknown = response?.headers?.['x-session-expires-at'];
            const expiresAtUtc: string | null = typeof rawHeader === 'string' && rawHeader.length > 0 ? rawHeader : null;
            if (expiresAtUtc !== null) {
                clientStore.setLoginState(true);
                clientStore.setExpiresAtUtcState(expiresAtUtc);
            } else {
                clientStore.setLoginState(false); // 沒有到期時間，代表 Session 已過期，強制登出
                clientStore.setExpiresAtUtcState(null);
            }
            // expiresAtUtc 同步給絕對到期 timer：null 會清除排程，有值且異動會重排
            GetSessionExpireTimerInstance().StartDetect(expiresAtUtc);
        } catch (err) {
            // session 同步屬側路邏輯，任何例外一律吞掉不擋主流程
            Common.ConsoleLog('[ApiClient] session header sync failed: ' + err);
        }

        return response;
    },
    (error) => {
        // enter error condition response http code
        // 1.server timeout
        // 2.error conncetion
        // 3.server exceptions ex.blocklogin..etc
        Common.ConsoleLog(`[ApiClient] Response Intercept error` + error);
        LoadingOverLay.hideAll();
        return Promise.reject(error);
    }
);

/**
 * @todo POST Function (停登訊息確認後，顯示API回應)
 * @param url
 * @param data
 * @returns
 */
export async function post(url: string, data: object = {}, config: object = {}): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
        Instance.post(url, data, config)
            .then(async (response) => {
                //1.閒置逾時偵測
                const skipIdletimeout: paramTypeEnum =
                    typeof response.config.params?.skipIdletimeout !== 'undefined' ? response.config.params?.skipIdletimeout : paramTypeEnum.false;
                Common.ConsoleLog('[ApiClient] Request Post() isSkipTimeoutDetection: ' + skipIdletimeout);
                Common.ConsoleLog('[ApiClient] Request Post() Get Response Start: ' + url);

                if (skipIdletimeout !== paramTypeEnum.true) {
                    Common.ConsoleLog('[ApiClient] Request Post() Get Response Start serviceAlert.IdletimeoutDetection');
                    await GetServiceAlertInstance().IdletimeoutDetection();
                }
                //2.顯示停登彈窗
                //3.當此次請求網址是確認閱讀service alert message，不顯示彈窗訊息
                const isDisplay: boolean = !(response.config.url === RouteUrlList.API_ConfirmServiceAlert.path);
                await GetServiceAlertInstance()
                    .ShowAlertMessage(isDisplay)
                    .then((res) => {
                        Common.ConsoleLog('[ApiClient-ShowAlertMessage] Request Post() Get Response End: ' + url);
                        resolve(response);
                    });
            })
            .catch((error) => {
                Common.ConsoleLog('[ApiClient] Request Post() Get Response End: ' + url);
                reject(error);
            });
    });
}

/**
 * @todo 停登訊息確認後，顯示API回應
 * @param url
 * @param data
 * @returns
 */
export async function get(url: string, config: object = {}): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
        Instance.get(url, config)
            .then(async (response) => {
                Common.ConsoleLog('[ApiClient] Get() Get Response : ' + url);
                resolve(response);
            })
            .catch((error) => {
                reject(error);
            });
    });
}

const defaultErrorMsg = {
    busy: '系統忙碌中 請稍候再試',
    unknown: '系統錯誤，請重新整理頁面',
    disconnect: '資料連線中斷，請回起始頁'
};

/**
 * @todo 處理 回應 200但，狀態status為false
 */
const HandleGeneralRespError = (res: any): Promise<any> => {
    const msg = res && res.message ? res.message : defaultErrorMsg.busy;
    return simplePopUp.showAlert(SimplePopUpModalType.Warning, '錯誤', msg, [
        {
            id: 'confirm',
            type: SimplePopUpBtnType.callback,
            btnClass: 'btn-info',
            resolveParam: 'confirm',
            title: '確認'
        }
    ]);
};

/**
 * ErrorCode [http 400] API 錯誤處理
 * @param {XMLHttpRequest} error
 */
const HandleError = (
    error: any | AxiosResponse<any>,
    isUseRoutePush: boolean = false,
    routeName: IRouteUrlListElement | null = RouteUrlList.Login,
    redirectUrl: string | null = null,
    isCloseWebView: boolean | null = null
): Promise<any> => {
    // 若未明確指定，自動偵測是否在 A+ WebView 內
    const shouldCloseWebView: boolean = isCloseWebView !== null ? isCloseWebView : isAppEmbedded.value;
    const defaultErrorTitle: string = '錯誤';
    const defaultConfirmText: string = '確認';

    if (error && error.response) {
        const ErrorCase =
            error.response.data && error.response.data.errorCode ? parseInt(error.response.data.errorCode.replace('E-', '')) : ErrorCode.Unknown;
        const title: string = error.response.data.title || defaultErrorTitle;
        const msg: string = error.response.data.message || defaultErrorMsg.unknown;
        Common.ConsoleLog(title + msg);
        const isIniFrame = window === window.parent ? false : true;

        const errorCode = 'E-' + ErrorCase.toString().padStart(3, '0');
        // Store Message in pinia
        try {
            const stateErrorMessageStore = useStateErrorMessageStore();
            // Example of setting the state
            stateErrorMessageStore.setStateErrorMessage({
                title: title,
                message: msg,
                errorCode: errorCode
            });
        } catch (ex) {
            Common.ConsoleLog(ex);
        }
        // Start Handle Error
        if (error.response.status === 401) {
            return simplePopUp
                .showAlert(
                    SimplePopUpModalType.Warning,
                    title,
                    '驗證失敗',
                    [
                        {
                            id: 'confirm',
                            type: SimplePopUpBtnType.callback,
                            btnClass: 'btn-info',
                            resolveParam: 'confirm',
                            title: defaultConfirmText
                        }
                    ],
                    SimplePopUpSize.lg,
                    true
                )
                .then(() => {
                    AfterPopUpAction(true, RouteUrlList.StateError, redirectUrl, shouldCloseWebView);
                });
        } else if (error.response.status === 403) {
            return simplePopUp.showAlert(SimplePopUpModalType.Warning, title, '未授權', [], SimplePopUpSize.lg, true).then(() => {
                AfterPopUpAction(true, RouteUrlList.StateError, redirectUrl, shouldCloseWebView);
            });
        } else if (ErrorCase === ErrorCode.ServiceMaintaining) {
            // Service Maintaining
            return simplePopUp
                .showAlert(SimplePopUpModalType.Warning, title, `${msg}`, [
                    {
                        id: 'confirm',
                        type: SimplePopUpBtnType.callback,
                        btnClass: 'btn-info',
                        resolveParam: 'confirm',
                        title: defaultConfirmText
                    }
                ])
                .then(() => {
                    AfterPopUpAction(true, RouteUrlList.StateError, redirectUrl, shouldCloseWebView);
                });
        } else if (ErrorCase === ErrorCode.SessionExpired || ErrorCase === ErrorCode.IllegalFlow) {
            return simplePopUp
                .showAlert(SimplePopUpModalType.Warning, title, `${msg}`, [
                    {
                        id: 'confirm',
                        type: SimplePopUpBtnType.callback,
                        btnClass: 'btn-info',
                        resolveParam: 'confirm',
                        title: defaultConfirmText
                    }
                ])
                .then(() => {
                    AfterPopUpAction(true, RouteUrlList.StateError, redirectUrl, shouldCloseWebView);
                });
        } else if (ErrorCase === ErrorCode.DayLimitReached) {
            // 很抱歉，您的簡訊/Email驗證碼發送次數已超過每日上線，請於隔日凌晨00:00後再進行線上補件，造成不便敬請見諒。
            return new Promise((resolve, reject) => {
                AfterPopUpAction(true, RouteUrlList.StateError, redirectUrl, shouldCloseWebView);
            });
        } else {
            // 其他 Error Code
            return simplePopUp
                .showAlert(SimplePopUpModalType.Warning, title, `${msg}`, [
                    {
                        id: 'confirm',
                        type: SimplePopUpBtnType.callback,
                        btnClass: 'btn-info',
                        resolveParam: 'confirm',
                        title: defaultConfirmText
                    }
                ])
                .then(() => {
                    AfterPopUpAction(isUseRoutePush, routeName, redirectUrl, shouldCloseWebView);
                });
        }
    } else if (error && error.request) {
        // The request was made and the server responded with a status code
        return simplePopUp
            .showAlert(SimplePopUpModalType.Warning, defaultErrorTitle, defaultErrorMsg.disconnect, [
                {
                    id: 'confirm',
                    type: SimplePopUpBtnType.callback,
                    btnClass: 'btn-info',
                    resolveParam: 'confirm',
                    title: defaultConfirmText
                }
            ])
            .then(() => {
                AfterPopUpAction(isUseRoutePush, routeName, redirectUrl, shouldCloseWebView);
            });
    } else {
        // no response
        return simplePopUp
            .showAlert(SimplePopUpModalType.Warning, defaultErrorTitle, defaultErrorMsg.unknown, [
                {
                    id: 'confirm',
                    type: SimplePopUpBtnType.callback,
                    btnClass: 'btn-info',
                    resolveParam: 'confirm',
                    title: defaultConfirmText
                }
            ])
            .then(() => {
                AfterPopUpAction(isUseRoutePush, routeName, redirectUrl, shouldCloseWebView);
            });
    }
};

/**
 * 彈窗確認後的導向動作
 * 依據環境判斷：關閉 WebView / 路由跳轉 / URL 導向
 */
function AfterPopUpAction(
    isUseRoutePush: boolean = false,
    routeName: IRouteUrlListElement | null = RouteUrlList.StateError,
    redirectUrl: string | null = null,
    shouldCloseWebView: boolean = false
) {
    if (shouldCloseWebView) {
        closeWebView();
    } else if (isUseRoutePush) {
        if (!routeName) {
            routeName = RouteUrlList.StateError;
        }
        router.replace({ name: routeName.name, query: { readlocally: 'y' } });
    } else if (redirectUrl) {
        window.location.href = redirectUrl;
    }
}

/**
 * 錯誤代碼清單
 * 此列表需與 DCS.FrontStage.Web\Libs\MyExceptionList.cs 保持一致
 */
enum ErrorCode {
    // 基本錯誤 (0-8)
    ServiceMaintaining = 0, // 服務維護中
    GetServiceAlertError = 1, // 取得service alert錯誤
    ModelBindingError = 2, // 參數綁定錯誤
    CaptchaError = 3, // 驗證碼錯誤
    SessionExpired = 4, // 作業逾時/Session過期
    IllegalFlow = 5, // 步驟錯誤
    HttpError = 6, // API打不通
    TaskCanceledError = 7, // 用戶斷線錯誤
    Unknown = 8, // 未預期錯誤

    // 登入相關錯誤 (9-15)
    GetAccountIDBySSN_Error = 9, // 身分證字號查無對應分公司
    CheckLoginError = 10, // 用帳號登入錯誤 / Unauthorized
    OtpApiError = 11, // OTP API錯誤
    DayLimitReached = 12, // 驗證次數已達上限
    HasMultiAccount = 15, // 偵測到多筆帳號

    // 資料獲取錯誤 (29-33)
    GetStockCustomerDataError = 29, // 取顧客資料錯誤
    CommonApiError = 30, // 常用API錯誤
    OcrApiError = 31, // OCR API錯誤
    DcsApiError = 32, // DCS API錯誤
    MOI_Error = 33, // MOI錯誤

    // 系統錯誤 (90-99)
    PayloadTooLargeError = 90, // 上傳資料超過大小限制
    GetClientIPError = 91, // IP解析錯誤
    AccessDenined = 99, // 無通過OTP驗證

    // 資源錯誤 (404)
    SourceNotFoundError = 404, // 資源不存在
    // 前端特有流程控制錯誤碼（保留原有邏輯）
    RedirectToFinishPage = 112, // 已完成申請，跳轉至申請進度查詢頁
    SkipContractPage = 113, // 已簽署契約，跳轉至下一階段契約書頁面
    SkipMarginContractPage = 114, // 已簽署融資融券契約書，跳轉至契約確認頁
    SkipConfirmContractPage = 117 // 已確認期貨開戶契約書，跳轉至身分證件上傳頁
}

/**
 * 模擬延遲後執行 callback
 * @param callback 要執行的函式（可為同步或非同步）
 * @param ms 延遲毫秒
 * @returns callback 執行結果（支援 Promise）
 */
const Delay = async <T>(callback: () => T | Promise<T>, ms: number): Promise<T> => {
    await new Promise<void>((resolve) => setTimeout(resolve, ms));
    return await callback();
};

export { config, Instance, defaultErrorMsg, HandleGeneralRespError, HandleError, GetServiceAlertInstance, ErrorCode, Delay };
